import { database } from './firebaseConfig';
import { ref, push, set, update, remove, get } from 'firebase/database';
import { getBackupRetentionPlan } from '../utils/backupRetention';
import { addMoney, normalizeMoney, subtractMoney } from '../utils/money';

const BACKUP_EXCLUDED_KEYS = ['backups', 'backupMetadata', 'backupMetadataBackfilled'];

const getBackupMetadata = (backup) => ({
  name: backup?.name || '',
  createdAt: backup?.createdAt || 0,
  type: backup?.type || 'manual',
});

const getSortedBackupMetadataList = (backups) => (
  Object.entries(backups || {})
    .map(([id, backup]) => ({ id, ...getBackupMetadata(backup) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
);

const backfillBackupMetadata = async (userId, backups) => {
  const metadataUpdates = Object.entries(backups || {}).reduce((acc, [id, backup]) => {
    acc[`users/${userId}/backupMetadata/${id}`] = getBackupMetadata(backup);
    return acc;
  }, {
    [`users/${userId}/backupMetadataBackfilled`]: true,
  });

  await update(ref(database), metadataUpdates);
};

const getBackupMetadataForUser = async (userId) => {
  const backfilledSnapshot = await get(ref(database, `users/${userId}/backupMetadataBackfilled`));

  if (backfilledSnapshot.val() === true) {
    const metadataSnapshot = await get(ref(database, `users/${userId}/backupMetadata`));
    return {
      backupsById: metadataSnapshot.val() || {},
      wasBackfilled: true,
    };
  }

  const backupSnapshot = await get(ref(database, `users/${userId}/backups`));
  const backups = backupSnapshot.val() || {};
  await backfillBackupMetadata(userId, backups);

  return {
    backupsById: backups,
    wasBackfilled: false,
  };
};

const buildUserBackupPayload = (userData) => {
  if (!userData || typeof userData !== 'object') {
    return {};
  }
  return Object.entries(userData).reduce((acc, [key, value]) => {
    if (BACKUP_EXCLUDED_KEYS.includes(key)) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
};

const buildReconciliationBaseDedupeKey = ({ date, amount, normalizedDescription }) => {
  const normalizedDate = date || '';
  const normalizedAmount = Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
  return `${normalizedDate}|${normalizedAmount}|${normalizedDescription || ''}`;
};

const buildReconciliationDedupeKey = ({ date, amount, normalizedDescription, importOccurrence = 1 }) => {
  const baseKey = buildReconciliationBaseDedupeKey({ date, amount, normalizedDescription });
  const occurrence = Number.isInteger(importOccurrence) && importOccurrence > 0 ? importOccurrence : 1;
  return `${baseKey}|${occurrence}`;
};

// Accounts
export const addAccount = async (userId, account) => {
  const newAccountRef = push(ref(database, `users/${userId}/accounts`));
  const accountPayload = {
    ...account,
    archived: account?.archived ?? false,
  };
  await set(newAccountRef, accountPayload);
  return newAccountRef.key;
};

export const archiveAccount = (userId, accountId) => {
  return update(ref(database, `users/${userId}/accounts/${accountId}`), {
    archived: true,
    archivedAt: new Date().toISOString(),
  });
};

export const restoreAccount = (userId, accountId) => {
  return update(ref(database, `users/${userId}/accounts/${accountId}`), {
    archived: false,
    archivedAt: null,
  });
};

export const updateAccount = (userId, accountId, accountUpdate) => {
  return update(ref(database, `users/${userId}/accounts/${accountId}`), accountUpdate);
};

// Transactions
export const addTransaction = (userId, transaction) => {
  const newTransactionRef = push(ref(database, `users/${userId}/transactions`));
  return set(newTransactionRef, transaction);
};

export const updateTransaction = (userId, transactionId, transactionUpdate, parentId = null) => {
  const transactionRef = parentId
    ? ref(database, `users/${userId}/transactions/${parentId}/childTransactions/${transactionId}`)
    : ref(database, `users/${userId}/transactions/${transactionId}`);
  return update(transactionRef, transactionUpdate);
};

export const markTransactionReconciliationMatch = async (
  userId,
  transactionId,
  parentId,
  accountId,
  reconciliationTransactionId,
  reconciled = true,
) => {
  if (!userId) throw new Error('markTransactionReconciliationMatch requires a userId');
  if (!transactionId) throw new Error('markTransactionReconciliationMatch requires a transactionId');
  if (!accountId) throw new Error('markTransactionReconciliationMatch requires an accountId');

  const transactionPath = parentId
    ? `users/${userId}/transactions/${parentId}/childTransactions/${transactionId}`
    : `users/${userId}/transactions/${transactionId}`;

  const updates = {};
  updates[`${transactionPath}/reconciliationMatches/${accountId}`] = reconciled
    ? {
        reconciliationTransactionId: reconciliationTransactionId || '',
        reconciledAt: Date.now(),
      }
    : null;

  return update(ref(database), updates);
};

export const addRecurringTransaction = async (userId, transaction) => {
  const newTransactionRef = push(ref(database, `users/${userId}/transactions`));
  await set(newTransactionRef, transaction);
  return newTransactionRef;
};

export const addRecurringTransactionWithChildren = async (
  userId,
  parentTransaction,
  childTransactions,
) => {
  const parentRef = push(ref(database, `users/${userId}/transactions`));
  const parentKey = parentRef.key;

  const childData = {};
  childTransactions.forEach((child) => {
    const childRef = push(
      ref(database, `users/${userId}/transactions/${parentKey}/childTransactions`)
    );
    childData[childRef.key] = { ...child, parentId: parentKey };
  });

  await set(parentRef, { ...parentTransaction, childTransactions: childData });

  return parentRef;
};

export const addChildTransaction = (userId, parentId, transaction) => {
  const newChildTransactionRef = push(ref(database, `users/${userId}/transactions/${parentId}/childTransactions`));
  return set(newChildTransactionRef, transaction);
};

export const deleteTransaction = async (userId, transactionId, parentId = null) => {
  const transactionRef = parentId 
    ? ref(database, `users/${userId}/transactions/${parentId}/childTransactions/${transactionId}`) 
    : ref(database, `users/${userId}/transactions/${transactionId}`);
  await remove(transactionRef);
};

export const deleteRecurringTransaction = async (userId, parentId) => {
  const parentTransactionRef = ref(database, `users/${userId}/transactions/${parentId}`);
  const snapshot = await get(parentTransactionRef);
  const parentTransactionData = snapshot.val();

  if (parentTransactionData && parentTransactionData.childTransactions) {
    const updates = {};
    Object.keys(parentTransactionData.childTransactions).forEach(childId => {
      updates[`users/${userId}/transactions/${parentId}/childTransactions/${childId}`] = null;
    });

    // Perform the child transactions deletion first
    await update(ref(database), updates);

    // Then remove the parent transaction
    await remove(parentTransactionRef);
  } else {
    await remove(parentTransactionRef);
  }
};

export const markTransactionPaid = async (userId, transactionId, parentId, fromAccount, toAccount, amount) => {
  const accountsSnapshot = await get(ref(database, `users/${userId}/accounts`));
  const accounts = accountsSnapshot.val() || {};

  const updates = {};
  const numericAmount = normalizeMoney(amount);

  if (fromAccount && accounts[fromAccount]) {
    const newBalance = subtractMoney(accounts[fromAccount].balance || 0, numericAmount);
    updates[`users/${userId}/accounts/${fromAccount}/balance`] = newBalance;
  }

  if (toAccount && accounts[toAccount]) {
    const newBalance = addMoney(accounts[toAccount].balance || 0, numericAmount);
    updates[`users/${userId}/accounts/${toAccount}/balance`] = newBalance;
  }

  const transactionPath = parentId
    ? `users/${userId}/transactions/${parentId}/childTransactions/${transactionId}`
    : `users/${userId}/transactions/${transactionId}`;

  updates[`${transactionPath}/isPaid`] = true;

  await update(ref(database), updates);
};

export const markTransactionUnpaid = async (userId, transactionId, parentId, fromAccount, toAccount, amount) => {
  const accountsSnapshot = await get(ref(database, `users/${userId}/accounts`));
  const accounts = accountsSnapshot.val() || {};

  const updates = {};
  const numericAmount = normalizeMoney(amount);

  if (fromAccount && accounts[fromAccount]) {
    const newBalance = addMoney(accounts[fromAccount].balance || 0, numericAmount);
    updates[`users/${userId}/accounts/${fromAccount}/balance`] = newBalance;
  }

  if (toAccount && accounts[toAccount]) {
    const newBalance = subtractMoney(accounts[toAccount].balance || 0, numericAmount);
    updates[`users/${userId}/accounts/${toAccount}/balance`] = newBalance;
  }

  const transactionPath = parentId
    ? `users/${userId}/transactions/${parentId}/childTransactions/${transactionId}`
    : `users/${userId}/transactions/${transactionId}`;

  updates[`${transactionPath}/isPaid`] = false;

  await update(ref(database), updates);
};

export const updateSingleTransactionWithPaidBalanceAdjustment = async (
  userId,
  originalTransaction,
  updatedTransaction
) => {
  if (!userId || !originalTransaction || !updatedTransaction?.id) return;

  const accountsSnapshot = await get(ref(database, `users/${userId}/accounts`));
  const accounts = accountsSnapshot.val() || {};
  const updates = {};

  const applyBalanceMovement = (transaction, direction) => {
    const amount = parseFloat(transaction.amount);
    if (!Number.isFinite(amount)) return;

    if (transaction.fromAccount && accounts[transaction.fromAccount]) {
      const path = `users/${userId}/accounts/${transaction.fromAccount}/balance`;
      const currentBalance = updates[path] ?? parseFloat(accounts[transaction.fromAccount].balance || 0);
      updates[path] = subtractMoney(currentBalance, amount * direction);
    }

    if (transaction.toAccount && accounts[transaction.toAccount]) {
      const path = `users/${userId}/accounts/${transaction.toAccount}/balance`;
      const currentBalance = updates[path] ?? parseFloat(accounts[transaction.toAccount].balance || 0);
      updates[path] = addMoney(currentBalance, amount * direction);
    }
  };

  if (originalTransaction.isPaid) {
    applyBalanceMovement(originalTransaction, -1);
  }

  if (updatedTransaction.isPaid) {
    applyBalanceMovement(updatedTransaction, 1);
  }

  const transactionPath = updatedTransaction.parentId
    ? `users/${userId}/transactions/${updatedTransaction.parentId}/childTransactions/${updatedTransaction.id}`
    : `users/${userId}/transactions/${updatedTransaction.id}`;

  const {
    id,
    parentId,
    editMode,
    ...transactionData
  } = updatedTransaction;

  updates[transactionPath] = {
    ...transactionData,
    parentId,
  };

  if (!parentId) {
    delete updates[transactionPath].parentId;
  }

  await update(ref(database), updates);
};

export const partialPayTransaction = async (
  userId,
  transactionId,
  parentId,
  fromAccount,
  toAccount,
  amountPaid,
  date,
  partialDescription
) => {
  if (!userId || !transactionId) {
    throw new Error('A transaction is required to apply a partial payment or refund.');
  }

  const numericAmount = normalizeMoney(amountPaid);
  if (!Number.isFinite(numericAmount) || numericAmount === 0) {
    throw new Error('Partial transaction amount must be a non-zero number.');
  }

  const transactionPath = parentId
    ? 'users/' + userId + '/transactions/' + parentId + '/childTransactions/' + transactionId
    : 'users/' + userId + '/transactions/' + transactionId;

  const [accountsSnapshot, transactionSnapshot] = await Promise.all([
    get(ref(database, 'users/' + userId + '/accounts')),
    get(ref(database, transactionPath)),
  ]);
  const accounts = accountsSnapshot.val() || {};
  const transactionData = transactionSnapshot.val();

  if (!transactionData) {
    throw new Error('The transaction could not be found.');
  }

  const currentAmount = normalizeMoney(transactionData.amount);
  if (!Number.isFinite(currentAmount)) {
    throw new Error('The transaction has an invalid remaining amount.');
  }
  if (numericAmount > 0 && numericAmount > currentAmount) {
    throw new Error('The payment cannot exceed the remaining amount of ' + currentAmount.toFixed(2) + '.');
  }

  const updates = {};

  if (fromAccount && accounts[fromAccount]) {
    const newBalance = subtractMoney(accounts[fromAccount].balance || 0, numericAmount);
    updates[`users/${userId}/accounts/${fromAccount}/balance`] = newBalance;
  }

  if (toAccount && accounts[toAccount]) {
    const newBalance = addMoney(accounts[toAccount].balance || 0, numericAmount);
    updates[`users/${userId}/accounts/${toAccount}/balance`] = newBalance;
  }

  const newAmount = subtractMoney(currentAmount, numericAmount);

  updates[`${transactionPath}/amount`] = newAmount;
  updates[`${transactionPath}/isPaid`] = newAmount === 0;


  const resolvedDescription =
    (partialDescription && partialDescription.trim()) ||
    transactionData.description ||
    'Partial Payment';
  const partialTransaction = {
    description: resolvedDescription,
    amount: numericAmount,
    fromAccount,
    toAccount,
    date,
    isPaid: true,
  };
  const newTransactionRef = push(ref(database, 'users/' + userId + '/transactions'));
  updates['users/' + userId + '/transactions/' + newTransactionRef.key] = partialTransaction;

  await update(ref(database), updates);
};

export const skipRecurringTransactionOccurrence = async (userId, transactionId, parentId) => {
  if (!parentId) {
    throw new Error('skipRecurringTransactionOccurrence called for non-recurring transaction');
  }

  const childTransactionsRef = ref(database, `users/${userId}/transactions/${parentId}/childTransactions`);
  const snapshot = await get(childTransactionsRef);
  const childTransactions = snapshot.val() || {};

  const entries = Object.entries(childTransactions).sort((a, b) => new Date(a[1].date) - new Date(b[1].date));
  const index = entries.findIndex(([id]) => id === transactionId);
  if (index === -1) {
    throw new Error('Transaction to skip not found');
  }

  const [currentId, currentTx] = entries[index];
  const nextEntry = entries[index + 1];

  const updates = {};

  if (nextEntry) {
    const [nextId, nextTx] = nextEntry;
    const newAmount = addMoney(nextTx.amount || 0, currentTx.amount || 0);
    updates[`users/${userId}/transactions/${parentId}/childTransactions/${nextId}/amount`] = newAmount;
  }

  updates[`users/${userId}/transactions/${parentId}/childTransactions/${currentId}`] = null;

  await update(ref(database), updates);
};

export const saveReconciliationSettings = async (userId, selectedAccounts) => {
  if (!userId) throw new Error('saveReconciliationSettings requires a userId');
  const settingsRef = ref(database, `users/${userId}/reconciliation/settings`);
  return update(settingsRef, { selectedAccounts: selectedAccounts || {} });
};

export const upsertReconciliationTransactions = async (userId, items) => {
  if (!userId) throw new Error('upsertReconciliationTransactions requires a userId');
  if (!Array.isArray(items) || items.length === 0) {
    return { added: [], updated: [], skipped: [] };
  }

  const transactionsRef = ref(database, `users/${userId}/reconciliation/transactions`);
  const snapshot = await get(transactionsRef);
  const existingData = snapshot.val() || {};

  const existingByCompositeKey = new Map();
  Object.entries(existingData).forEach(([id, txn]) => {
    if (!txn || !txn.accountId) {
      return;
    }
    const baseDedupeKey = txn.baseDedupeKey || txn.dedupeKey;
    if (!baseDedupeKey) {
      return;
    }
    const compositeKey = `${txn.accountId}|${baseDedupeKey}`;
    const existing = existingByCompositeKey.get(compositeKey) || [];
    existing.push({ id, txn });
    existingByCompositeKey.set(compositeKey, existing);
  });

  existingByCompositeKey.forEach((bucket) => {
    bucket.sort((a, b) => {
      const occurrenceA = Number(a.txn.importOccurrence) || 1;
      const occurrenceB = Number(b.txn.importOccurrence) || 1;
      if (occurrenceA !== occurrenceB) return occurrenceA - occurrenceB;
      const importedA = Number(a.txn.importedAt) || 0;
      const importedB = Number(b.txn.importedAt) || 0;
      if (importedA !== importedB) return importedA - importedB;
      return a.id.localeCompare(b.id);
    });
  });

  const updates = {};
  const added = [];
  const updated = [];
  const skipped = [];
  const importOccurrences = new Map();

  items.forEach((item) => {
    if (!item || !item.accountId || !item.date || typeof item.amount !== 'number') {
      skipped.push(item);
      return;
    }

    const normalizedDescription = (item.normalizedDescription || '').trim();
    const baseDedupeKey = buildReconciliationBaseDedupeKey({
      date: item.date,
      amount: item.amount,
      normalizedDescription,
    });
    const compositeKey = `${item.accountId}|${baseDedupeKey}`;
    const importOccurrence = (importOccurrences.get(compositeKey) || 0) + 1;
    importOccurrences.set(compositeKey, importOccurrence);
    const dedupeKey = buildReconciliationDedupeKey({
      date: item.date,
      amount: item.amount,
      normalizedDescription,
      importOccurrence,
    });

    const existing = existingByCompositeKey.get(compositeKey)?.[importOccurrence - 1];
    if (existing) {
      const basePath = `users/${userId}/reconciliation/transactions/${existing.id}`;
      updates[`${basePath}/description`] = item.description || '';
      updates[`${basePath}/rawDescription`] = item.rawDescription ?? item.description ?? '';
      updates[`${basePath}/normalizedDescription`] = normalizedDescription;
      updates[`${basePath}/date`] = item.date;
      updates[`${basePath}/amount`] = item.amount;
      updates[`${basePath}/pending`] = Boolean(item.pending);
      updates[`${basePath}/lastImportedAt`] = item.importedAt || Date.now();
      updates[`${basePath}/accountName`] = item.accountName || existing.txn.accountName || '';
      updates[`${basePath}/baseDedupeKey`] = baseDedupeKey;
      updates[`${basePath}/dedupeKey`] = dedupeKey;
      updates[`${basePath}/importOccurrence`] = importOccurrence;
      updated.push(existing.id);
      return;
    }

    const newRef = push(transactionsRef);
    const newPath = `users/${userId}/reconciliation/transactions/${newRef.key}`;
    updates[newPath] = {
      accountId: item.accountId,
      accountName: item.accountName || '',
      date: item.date,
      amount: item.amount,
      description: item.description || '',
      rawDescription: item.rawDescription ?? item.description ?? '',
      normalizedDescription,
      baseDedupeKey,
      dedupeKey,
      importOccurrence,
      pending: Boolean(item.pending),
      importedAt: item.importedAt || Date.now(),
      reconciled: false,
    };
    added.push(newRef.key);
  });

  if (Object.keys(updates).length > 0) {
    await update(ref(database), updates);
  }

  return { added, updated, skipped };
};

export const markReconciliationTransaction = async (userId, transactionId, reconciled) => {
  if (!userId) throw new Error('markReconciliationTransaction requires a userId');
  if (!transactionId) throw new Error('markReconciliationTransaction requires a transactionId');

  const updates = {};
  const basePath = `users/${userId}/reconciliation/transactions/${transactionId}`;
  updates[`${basePath}/reconciled`] = reconciled;
  updates[`${basePath}/reconciledAt`] = reconciled ? Date.now() : null;
  return update(ref(database), updates);
};



export const updateUserSettings = (userId, settingsUpdate) => {
  return update(ref(database, `users/${userId}/settings`), settingsUpdate);
};

export const updateUserProfile = (userId, profileUpdate) => {
  return update(ref(database, `users/${userId}/profile`), profileUpdate);
};

const createBackupRecord = async (userId, payload) => {
  const backupListRef = ref(database, `users/${userId}/backups`);
  const newBackupRef = push(backupListRef);
  const updates = {
    [`users/${userId}/backups/${newBackupRef.key}`]: payload,
    [`users/${userId}/backupMetadata/${newBackupRef.key}`]: getBackupMetadata(payload),
  };
  await update(ref(database), updates);
  return { id: newBackupRef.key, ...payload };
};

const pruneAutomaticBackups = async (userId, backups) => {
  const backupList = Object.entries(backups || {}).map(([id, backup]) => ({ id, ...backup }));
  const { deleteIds } = getBackupRetentionPlan(backupList);

  if (deleteIds.length === 0) {
    return [];
  }

  const updates = deleteIds.reduce((acc, backupId) => {
    acc[`users/${userId}/backups/${backupId}`] = null;
    acc[`users/${userId}/backupMetadata/${backupId}`] = null;
    return acc;
  }, {});

  await update(ref(database), updates);
  return deleteIds;
};

export const createBackup = async (userId, { name, automatic = false } = {}) => {
  if (!userId) {
    throw new Error('createBackup requires a userId');
  }

  const userSnapshot = await get(ref(database, `users/${userId}`));
  const rawUserData = userSnapshot.val() || {};
  const dataToBackup = buildUserBackupPayload(rawUserData);

  const now = Date.now();
  const resolvedName =
    (name && name.trim()) ||
    `${automatic ? 'Automatic' : 'Manual'} Backup ${new Date(now).toLocaleString()}`;

  return createBackupRecord(userId, {
    name: resolvedName,
    createdAt: now,
    type: automatic ? 'automatic' : 'manual',
    data: dataToBackup,
  });
};

export const listBackups = async (userId) => {
  if (!userId) {
    throw new Error('listBackups requires a userId');
  }

  const { backupsById } = await getBackupMetadataForUser(userId);
  return getSortedBackupMetadataList(backupsById);
};

export const deleteBackup = async (userId, backupId) => {
  if (!userId) {
    throw new Error('deleteBackup requires a userId');
  }
  if (!backupId) {
    throw new Error('deleteBackup requires a backupId');
  }
  await update(ref(database), {
    [`users/${userId}/backups/${backupId}`]: null,
    [`users/${userId}/backupMetadata/${backupId}`]: null,
  });
};

export const restoreBackup = async (userId, backupId) => {
  if (!userId) {
    throw new Error('restoreBackup requires a userId');
  }
  if (!backupId) {
    throw new Error('restoreBackup requires a backupId');
  }

  const backupRef = ref(database, `users/${userId}/backups/${backupId}`);
  const backupSnapshot = await get(backupRef);
  if (!backupSnapshot.exists()) {
    throw new Error('Backup not found');
  }

  const backupData = backupSnapshot.val();
  const payload = backupData?.data || {};

  const userSnapshot = await get(ref(database, `users/${userId}`));
  const currentData = userSnapshot.val() || {};

  const updates = {};

  Object.keys(currentData).forEach((key) => {
    if (BACKUP_EXCLUDED_KEYS.includes(key)) {
      return;
    }
    if (!(key in payload)) {
      updates[`users/${userId}/${key}`] = null;
    }
  });

  Object.entries(payload).forEach(([key, value]) => {
    updates[`users/${userId}/${key}`] = value ?? null;
  });

  if (Object.keys(updates).length > 0) {
    await update(ref(database), updates);
  }
};

export const ensureAutomaticBackup = async (
  userId,
  { minIntervalMs = 24 * 60 * 60 * 1000 } = {}
) => {
  if (!userId) {
    throw new Error('ensureAutomaticBackup requires a userId');
  }

  const { backupsById: backups } = await getBackupMetadataForUser(userId);
  const now = Date.now();

  const latestAutomatic = Object.values(backups)
    .filter((backup) => backup?.type === 'automatic')
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0];

  if (latestAutomatic && now - (latestAutomatic.createdAt || 0) < minIntervalMs) {
    await pruneAutomaticBackups(userId, backups);
    return null;
  }

  const backup = await createBackup(userId, { automatic: true });
  await pruneAutomaticBackups(userId, {
    ...backups,
    [backup.id]: backup,
  });
  return backup;
};
